import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { NeonEdge as NeonEdgeAtom } from '@sorare/core/src/atoms/ui/NeonEdge';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { OpenBuyGemsDialog_Context } from '@sorare/core/src/protos/events/so5/shared/events';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import gems from '#src/assets/gems/gems.webp';

const bobAnimation = keyframes`
  0% {
    transform: translateY(calc(-1 * var(--unit)));
  }
  50% {
    transform: translateY(calc(-0.5 * var(--unit)));
  }
  100% {
    transform: translateY(calc(-1 * var(--unit)));
  }
`;

const bobFloatAnimation = keyframes`
  100% {
    transform: translateY(calc(-1 * var(--unit)));
  }
`;

const Root = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 100%),
    repeating-linear-gradient(
      to left,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--double-and-a-half-unit)
    ),
    repeating-linear-gradient(
      to bottom,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--double-and-a-half-unit)
    ),
    linear-gradient(180deg, #284b30 0%, rgba(0, 0, 0, 0.7) 100%);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  overflow: hidden;
`;

const NeonEdge = styled(NeonEdgeAtom)`
  width: 100%;
`;
const Main = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  position: relative;
  z-index: 2;
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const Img = styled.img`
  width: calc(12 * var(--unit));
  height: calc(12 * var(--unit));
  object-fit: contain;
  object-position: center;
  margin: 0 auto;
  transition: 0.3s ease-in-out;
  ${Root}:hover & {
    animation-name: ${bobFloatAnimation}, ${bobAnimation};
    animation-duration: 0.3s, 1.5s;
    animation-delay: 0s, 0.3s;
    animation-timing-function: ease-out, ease-in-out;
    animation-iteration-count: 1, infinite;
    animation-fill-mode: forwards;
    animation-direction: normal, alternate;
  }
`;

export const BuyGemsButtonBlock = () => {
  const openDialog = useOpenDialog();
  const track = useEvents();
  return (
    <Root
      onClick={() => {
        openDialog(DialogKey.buyGems, 'true');
        track('Open Buy Gems Dialog', {
          context: OpenBuyGemsDialog_Context.BUY_GEMS_BUTTON_BLOCK,
        });
      }}
    >
      <NeonEdge side="top" showNeon="always" color="#8CE456">
        <Main>
          <BrandM bold>
            <FormattedMessage
              id="BuyGemsButtonBlock.title"
              defaultMessage="Gems"
            />
          </BrandM>

          <Img src={gems} alt="Gems" />
        </Main>
      </NeonEdge>
    </Root>
  );
};
